import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  getPromptSegments,
  getPromptSegmentDetail,
  createPromptVersion,
  activatePromptVersion,
  previewPromptSegment,
  getPromptPlaceholders,
  getPromptNotifications,
  ackPromptNotification,
} from '../../api/admin';
import AdminLayout from '../../components/AdminLayout';
import Button from '../../components/Button';
import Alert from '../../components/Alert';

// V4 M22 / F.3: Prompt 后台
// 后端: apps/ai_engine/admin_prompt_views.py (IsAdminRole — enterprise_admin 403)
// 5 道安全网（设计文档 8.3）：
//   1. 版本控制 — 保存自动 +1，DB 永久保留
//   2. 一键回滚 — 历史版本激活按钮
//   3. 真 AI 预览 — 跑一次 chat_json 看输出（节流 60s/段；草稿不落库；失败不写 PromptParseFailure）
//   4. 自动 fallback — 后端连续 3 次解析失败自动回滚 + SuperAdminNotification banner
//   5. 占位符面板 — 列出每段 ctx 可用字段

const SEGMENT_LABEL_FALLBACK = {
  portrait: '综合得分画像',
  overview: '能力概览',
  strength_card: '优势卡',
  weakness_analysis: '欠缺解析',
  dim_summary: '维度总结',
  final_eval: '综合评价',
  v5_ability_question: 'V5 能力题出题',
  v5_trait_question: 'V5 素质题出题',
  v5_report: 'V5 粗略报告',
  v5_detailed_report: 'V5 详细报告',
};

export default function AdminPromptsPage() {
  const [segments, setSegments] = useState([]);
  // 后端 /segments/ 现在只返 V5 4 段（V4 已隐），首段交给后端列表自动决定
  const [activeSegment, setActiveSegment] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showAllVersions, setShowAllVersions] = useState(false);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingMsg, setSavingMsg] = useState('');
  const [error, setError] = useState('');
  const [placeholders, setPlaceholders] = useState([]);
  const [notifications, setNotifications] = useState([]);

  // 预览状态
  const [previewing, setPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);

  const placeholdersForSegment = useMemo(() => {
    const block = placeholders.find((p) => p.segment === activeSegment);
    return block?.placeholders || [];
  }, [placeholders, activeSegment]);

  const loadSegments = useCallback(async () => {
    try {
      const { data } = await getPromptSegments();
      setSegments(data);
      // 首次加载或当前 activeSegment 不在后端列表里（如 V4 段已被服务端隐藏）
      // → 自动定位到列表第一段
      setActiveSegment((prev) => {
        if (!data || data.length === 0) return prev;
        const codes = data.map((s) => s.segment);
        return prev && codes.includes(prev) ? prev : data[0].segment;
      });
    } catch (err) {
      const status = err.response?.status;
      if (status === 403) {
        setError('当前账号无权访问 Prompt 后台（仅 admin / superadmin）');
      } else {
        setError('加载段汇总失败：' + (err.response?.data?.detail || err.message));
      }
    }
  }, []);

  const loadDetail = useCallback(async (segment, all = false) => {
    try {
      const { data } = await getPromptSegmentDetail(
        segment, all ? { all: 1 } : undefined,
      );
      setDetail(data);
      const active = data.versions.find((v) => v.is_active);
      setDraft(active?.prompt_text || '');
      setPreviewResult(null);
    } catch (err) {
      setError('加载段详情失败：' + (err.response?.data?.detail || err.message));
    }
  }, []);

  const loadPlaceholders = useCallback(async () => {
    try {
      const { data } = await getPromptPlaceholders();
      setPlaceholders(data);
    } catch { /* 占位符面板可降级 */ }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const { data } = await getPromptNotifications();
      setNotifications(data);
    } catch { /* banner 可降级 */ }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadSegments(), loadPlaceholders(), loadNotifications()]);
      setLoading(false);
    })();
  }, [loadSegments, loadPlaceholders, loadNotifications]);

  useEffect(() => {
    if (activeSegment) {
      loadDetail(activeSegment, showAllVersions);
    }
  }, [activeSegment, showAllVersions, loadDetail]);

  // 切段时复位"展开全部"状态
  useEffect(() => {
    setShowAllVersions(false);
  }, [activeSegment]);

  const handleSave = async () => {
    if (!draft.trim()) {
      setSavingMsg('prompt 不能为空');
      return;
    }
    setSavingMsg('保存中…');
    try {
      const { data } = await createPromptVersion(activeSegment, {
        prompt_text: draft, set_active: true,
      });
      setSavingMsg(`已保存为 v${data.version}（已激活）`);
      await Promise.all([loadSegments(), loadDetail(activeSegment)]);
    } catch (err) {
      setSavingMsg('保存失败：' + (err.response?.data?.detail || err.message));
    }
  };

  const handleActivate = async (version) => {
    if (!window.confirm(`回滚 ${SEGMENT_LABEL_FALLBACK[activeSegment]} 到 v${version}?`)) return;
    try {
      await activatePromptVersion(activeSegment, version);
      await Promise.all([loadSegments(), loadDetail(activeSegment)]);
      setSavingMsg(`已激活 v${version}`);
    } catch (err) {
      setSavingMsg('激活失败：' + (err.response?.data?.detail || err.message));
    }
  };

  const handlePreview = async () => {
    if (!draft.trim()) {
      setPreviewResult({ ok: false, error: 'prompt 不能为空' });
      return;
    }
    setPreviewing(true);
    setPreviewResult(null);
    try {
      const { data } = await previewPromptSegment(activeSegment, draft);
      setPreviewResult(data);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        setPreviewResult({
          ok: false,
          error: err.response?.data?.detail || '预览节流：60s 内每段仅可调用一次',
        });
      } else {
        setPreviewResult({
          ok: false,
          error: err.response?.data?.detail || err.message,
        });
      }
    } finally {
      setPreviewing(false);
    }
  };

  const handleAckNotification = async (id) => {
    try {
      await ackPromptNotification(id);
      await loadNotifications();
    } catch (err) {
      setSavingMsg('确认通知失败：' + (err.response?.data?.detail || err.message));
    }
  };

  const insertPlaceholder = (key) => {
    setDraft((prev) => `${prev}{${key}}`);
  };

  if (loading) {
    return (
      <AdminLayout title="Prompt 配置">
        <div style={{ padding: 40 }}>加载中…</div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Prompt 配置">
        <div style={{ padding: 24 }}>
          <Alert variant="error">{error}</Alert>
        </div>
      </AdminLayout>
    );
  }

  const activeSegmentMeta = segments.find((s) => s.segment === activeSegment);

  return (
    <AdminLayout title="Prompt 配置">
      <div style={{ padding: 20 }}>
        {/* 自动回滚通知 banner */}
        {notifications.length > 0 && (
          <Alert variant="warning" style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              ⚠️ 有 {notifications.length} 条 prompt 自动回滚通知未确认
            </div>
            {notifications.slice(0, 3).map((n) => (
              <div key={n.id} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4, fontSize: 13 }}>
                <span style={{ flex: 1 }}>{n.message}</span>
                <button
                  onClick={() => handleAckNotification(n.id)}
                  style={{ background: '#fff', border: '1px solid #f59e0b', color: '#92400e', borderRadius: 4, padding: '2px 10px', fontSize: 12, cursor: 'pointer' }}
                >
                  已知悉
                </button>
              </div>
            ))}
          </Alert>
        )}

        {/* 6 段 tab */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap', borderBottom: '1px solid #e2e8f0' }}>
          {segments.map((s) => {
            const isActive = s.segment === activeSegment;
            return (
              <button
                key={s.segment}
                onClick={() => setActiveSegment(s.segment)}
                data-testid={`prompt-tab-${s.segment}`}
                style={{
                  padding: '10px 16px',
                  background: isActive ? '#1a3a6b' : '#f8fafc',
                  color: isActive ? '#fff' : '#475569',
                  border: 'none', borderTop: '2px solid transparent',
                  borderTopColor: isActive ? '#1a3a6b' : 'transparent',
                  cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}
              >
                {s.segment_label}
                {' '}
                <span style={{ fontSize: 11, opacity: 0.7 }}>
                  v{s.active_version || '?'} / 共 {s.total_versions}
                </span>
              </button>
            );
          })}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16 }}>
          {/* 左：textarea + 操作 + 预览输出 */}
          <div>
            <div style={{ marginBottom: 8, fontSize: 13, color: '#64748b' }}>
              当前激活：v{activeSegmentMeta?.active_version || '?'} ·
              共 {activeSegmentMeta?.total_versions || 0} 个版本
            </div>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="输入 prompt 文本（system message）..."
              style={{
                width: '100%', minHeight: 360, padding: 12, fontFamily: 'monospace',
                fontSize: 13, lineHeight: 1.6, border: '1px solid #cbd5e1',
                borderRadius: 6, resize: 'vertical',
              }}
            />

            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <Button onClick={handleSave}>保存为新版本（立即激活）</Button>
              <Button variant="secondary" onClick={handlePreview} loading={previewing}>
                预览测试（真 AI 调用）
              </Button>
              {savingMsg && <span style={{ alignSelf: 'center', color: '#475569', fontSize: 13 }}>{savingMsg}</span>}
            </div>

            {/* 预览结果 */}
            {previewResult && (
              <div
                data-testid="prompt-preview-result"
                style={{
                  marginTop: 16, padding: 16, borderRadius: 6,
                  background: previewResult.ok ? '#f0fdf4' : '#fef2f2',
                  border: `1px solid ${previewResult.ok ? '#86efac' : '#fca5a5'}`,
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 8, color: previewResult.ok ? '#166534' : '#991b1b' }}>
                  {previewResult.ok ? '✅ AI 调用 + 解析成功' : '❌ AI 调用 / 解析失败'}
                  {previewResult.model_used && (
                    <span style={{ marginLeft: 12, fontSize: 12, fontWeight: 400, color: '#64748b' }}>
                      model: {previewResult.model_used} · {previewResult.elapsed_ms}ms
                    </span>
                  )}
                </div>
                {previewResult.ok ? (
                  <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(previewResult.ai_output, null, 2)}
                  </pre>
                ) : (
                  <div>
                    {previewResult.error && (
                      <div style={{ fontSize: 12, color: '#991b1b', marginBottom: 6 }}>
                        {previewResult.error}
                      </div>
                    )}
                    {previewResult.raw_output && (
                      <details>
                        <summary style={{ fontSize: 12, cursor: 'pointer', color: '#475569' }}>
                          raw_output（运营自查）
                        </summary>
                        <pre style={{ margin: '8px 0 0', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {previewResult.raw_output}
                        </pre>
                      </details>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 历史版本列表 */}
            <div style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>历史版本</h3>
                <span style={{ fontSize: 12, color: '#64748b' }}>
                  共 {detail?.total || 0} 条
                  {detail?.truncated && (
                    <>
                      ，仅显示最近 {detail.limit} 条 ·{' '}
                      <button
                        onClick={() => setShowAllVersions(true)}
                        style={{ background: 'none', border: 'none', color: '#1a3a6b', textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: 12 }}
                      >
                        查看全部
                      </button>
                    </>
                  )}
                </span>
              </div>
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                    <th style={{ padding: '6px 8px' }}>版本</th>
                    <th style={{ padding: '6px 8px' }}>状态</th>
                    <th style={{ padding: '6px 8px' }}>保存人</th>
                    <th style={{ padding: '6px 8px' }}>保存时间</th>
                    <th style={{ padding: '6px 8px' }}>自动回滚次数</th>
                    <th style={{ padding: '6px 8px' }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail?.versions || []).map((v) => (
                    <tr key={v.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '6px 8px', fontWeight: 600 }}>v{v.version}</td>
                      <td style={{ padding: '6px 8px' }}>
                        {v.is_active && <span style={{ background: '#dcfce7', color: '#166534', borderRadius: 4, padding: '1px 6px', fontSize: 11 }}>当前激活</span>}
                        {v.is_stable && <span style={{ marginLeft: 4, background: '#dbeafe', color: '#1e3a8a', borderRadius: 4, padding: '1px 6px', fontSize: 11 }}>stable</span>}
                      </td>
                      <td style={{ padding: '6px 8px', color: '#64748b' }}>{v.saved_by_email || '-'}</td>
                      <td style={{ padding: '6px 8px', color: '#64748b' }}>{v.saved_at?.slice(0, 19).replace('T', ' ')}</td>
                      <td style={{ padding: '6px 8px', color: v.auto_rolled_back_count > 0 ? '#dc2626' : '#64748b' }}>
                        {v.auto_rolled_back_count}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        {!v.is_active && (
                          <button
                            onClick={() => handleActivate(v.version)}
                            style={{ background: '#fff', border: '1px solid #1a3a6b', color: '#1a3a6b', borderRadius: 4, padding: '2px 10px', fontSize: 12, cursor: 'pointer' }}
                          >
                            回滚到此版本
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 右：占位符 reference 面板 */}
          <div style={{ background: '#f8fafc', borderRadius: 6, padding: 12, alignSelf: 'flex-start', position: 'sticky', top: 16 }}>
            <h4 style={{ fontSize: 13, fontWeight: 600, margin: '0 0 8px', color: '#1e293b' }}>
              占位符 reference
            </h4>
            <div style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', borderRadius: 4, padding: 8, marginBottom: 8, lineHeight: 1.5 }}>
              ⚠️ 这些字段会以 <strong>user JSON</strong> 形式传给 AI（与 prompt 同次调用）。
              <br />
              <strong>{'`{industry}`'} 等花括号写法不会被代码自动替换</strong>，原文交给 AI——
              建议在 prompt 里直接用字段名引导（例："参考 user JSON 的 industry 字段"）。
            </div>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
              点击下方按钮可在 prompt 末尾插入字段名。
            </div>
            {placeholdersForSegment.length === 0 ? (
              <div style={{ fontSize: 12, color: '#94a3b8' }}>无占位符</div>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {placeholdersForSegment.map((p) => (
                  <li key={p.key} style={{ marginBottom: 6 }}>
                    <button
                      onClick={() => insertPlaceholder(p.key)}
                      style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 4, padding: '4px 8px', fontSize: 12, cursor: 'pointer', width: '100%', textAlign: 'left' }}
                    >
                      <div style={{ fontFamily: 'monospace', color: '#1a3a6b', fontWeight: 600 }}>{`{${p.key}}`}</div>
                      <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{p.description}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
